import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CardTemplatePreview } from '@/components/CardTemplatePreview';
import { useCardTemplates } from '@/hooks/useCardTemplates';
import { cn } from '@/lib/utils';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useProductSlugTranslationsData, getLocalizedProductUrl } from '@/hooks/useProductSlugTranslations';
import { getLocalizedProductName } from '@/lib/imageAltTexts';

const featuredCarouselI18n: Record<string, { title: string; subtitle: string; viewAll: string }> = {
  en: { title: "Fake ID's for €49", subtitle: 'Premium quality cards with holograms and scannable features', viewAll: 'View All Cards' },
  de: { title: 'Fake Ausweise für €49', subtitle: 'Premium-Qualität mit Hologrammen und scannbaren Features', viewAll: 'Alle Karten ansehen' },
  es: { title: 'IDs Falsos por €49', subtitle: 'Tarjetas de calidad premium con hologramas y funciones escaneables', viewAll: 'Ver todas las tarjetas' },
  fr: { title: "Fausses cartes d'identité à €49", subtitle: 'Cartes de qualité premium avec hologrammes et codes scannables', viewAll: 'Voir toutes les cartes' },
  pt: { title: 'IDs Falsos por €49', subtitle: 'Cartões de qualidade premium com hologramas e recursos escaneáveis', viewAll: 'Ver todos os cartões' },
  ru: { title: 'Поддельные удостоверения за €49', subtitle: 'Карты премиум-качества с голограммами и сканируемыми элементами', viewAll: 'Все карты' },
  it: { title: 'Documenti Falsi a €49', subtitle: 'Carte di qualità premium con ologrammi e funzionalità scansionabili', viewAll: 'Vedi tutte le carte' },
  sv: { title: 'Falska ID för €49', subtitle: 'Premiumkvalitetskort med hologram och skannbara funktioner', viewAll: 'Visa alla kort' },
  zh: { title: '仿真证件 €49', subtitle: '配备全息图和可扫描功能的优质卡片', viewAll: '查看所有卡片' },
};

export const FeaturedProductsCarousel: React.FC = () => {
  const { language } = useLanguage();
  const { data: templates = [] } = useCardTemplates();
  const { data: slugTranslations = [] } = useProductSlugTranslationsData();
  const i18n = featuredCarouselI18n[language] || featuredCarouselI18n.en;

  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
  });
  
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const onInit = useCallback(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onInit();
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('reInit', onInit);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('reInit', onInit);
    };
  }, [emblaApi, onSelect, onInit]);

  // Auto-play
  useEffect(() => {
    if (!emblaApi) return;
    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 4000);
    return () => clearInterval(interval);
  }, [emblaApi]);

  const featuredTemplates = templates.slice(0, 6);

  const getProductUrl = (template: typeof templates[0]) => {
    const localized = getLocalizedProductUrl(slugTranslations, template.slug, language);
    if (localized) return localized;
    return `/shop/id-card-generator/${template.slug}`;
  };

  return (
    <section className="pt-12 pb-6 md:pt-20 md:pb-10 lg:pt-28 lg:pb-14 bg-background">
      <div className="container">
        <div className="text-center mb-8 md:mb-12 space-y-2">
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-black uppercase">
            {i18n.title.includes('€49') 
              ? <>{i18n.title.split('€49')[0]}<span className="text-primary">€49</span>{i18n.title.split('€49')[1]}</>
              : i18n.title}
          </h2>
          <p className="text-muted-foreground text-sm md:text-base">
            {i18n.subtitle}
          </p>
        </div>
        
        {/* Carousel */}
        <div className="relative">
          {/* Gradient Fade Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-8 md:w-24 lg:w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-8 md:w-24 lg:w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          
          {/* Navigation Arrows */}
          <button 
            onClick={scrollPrev}
            className={cn(
              "absolute left-1 md:left-4 top-[35%] md:top-[38%] -translate-y-1/2 z-20 w-9 h-9 md:w-12 md:h-12 rounded-full border-2 border-foreground/30 flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300 hover:scale-110 group",
              !canScrollPrev && "opacity-50"
            )}
            aria-label="Previous"
          >
            <ChevronLeft className="h-4 w-4 md:h-6 md:w-6 text-foreground/70 group-hover:text-primary-foreground transition-colors" />
          </button>
          
          <button 
            onClick={scrollNext}
            className={cn(
              "absolute right-1 md:right-4 top-[35%] md:top-[38%] -translate-y-1/2 z-20 w-9 h-9 md:w-12 md:h-12 rounded-full border-2 border-foreground/30 flex items-center justify-center hover:bg-primary hover:border-primary transition-all duration-300 hover:scale-110 group",
              !canScrollNext && "opacity-50"
            )}
            aria-label="Next"
          >
            <ChevronRight className="h-4 w-4 md:h-6 md:w-6 text-foreground/70 group-hover:text-primary-foreground transition-colors" />
          </button>

          {/* Embla Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {featuredTemplates.map((template) => (
                <div 
                  key={template.id}
                  className="flex-shrink-0 w-[75%] sm:w-[calc(100%/1.5)] md:w-[calc(100%/2.5)] lg:w-[calc(100%/3.15)] px-2 md:px-4"
                >
                  <LocalizedLink 
                    to={getProductUrl(template)}
                    className="block group"
                  >
                    <div className="space-y-3 md:space-y-4">
                      <div className="aspect-[1.6/1] rounded-lg md:rounded-xl overflow-hidden border border-border group-hover:border-primary/50 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/10">
                        <CardTemplatePreview 
                          frontImage={template.thumbnail_front_url || template.front_image_url}
                          backImage={template.thumbnail_back_url || template.back_image_url}
                          name={template.name}
                          priority={true}
                        />
                      </div>
                      <div className="text-center">
                        <h3 className="font-semibold text-base md:text-lg text-foreground group-hover:text-primary transition-colors">
                          {getLocalizedProductName(template.slug, language as any)}
                        </h3>
                        <p className="text-primary font-display font-bold text-lg md:text-xl">
                          €{template.price}
                        </p>
                      </div>
                    </div>
                  </LocalizedLink>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Pagination Dots */}
        <div className="flex justify-center gap-0 mt-4 md:mt-6">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              className="p-1.5 group"
              aria-label={`Go to slide ${index + 1}`}
            >
              <span className={cn(
                "block h-2 md:h-2.5 rounded-full transition-all duration-300",
                selectedIndex === index 
                  ? "bg-primary w-6 md:w-8" 
                  : "bg-muted-foreground/30 group-hover:bg-muted-foreground/50 w-2 md:w-2.5"
              )} />
            </button>
          ))}
        </div>
        
        <div className="text-center mt-8 md:mt-12">
          <Button variant="default" size="lg" asChild className="px-8 md:px-10">
            <LocalizedLink to="/shop">
              {i18n.viewAll}
            </LocalizedLink>
          </Button>
        </div>
      </div>
    </section>
  );
};
